import React from 'react';
import { Box, Container, SimpleGrid, Text, Image, Heading, VStack, useColorModeValue, Icon, Badge, HStack } from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { FaCar, FaStar, FaCheckCircle, FaArrowRight } from 'react-icons/fa';
import Honda from '../../images/CarBrands/honda.png';
import Toyota from '../../images/CarBrands/toyota.png';
import Diahatsu from '../../images/CarBrands/diahatsu.png';
import BMW from '../../images/CarBrands/bmw.jpg';
import Mercedes from '../../images/CarBrands/mercedes.png';
import Audi from '../../images/CarBrands/audi.png';
import Suzuki from '../../images/CarBrands/suzuki.jpg';
import Chevroltet from '../../images/CarBrands/chevrolet.jpg';
import Hyundai from '../../images/CarBrands/hyundai.jpg';
import Mg from '../../images/CarBrands/mg.png';
import Peugeot from '../../images/CarBrands/peugeot.png';
import Nissan from '../../images/CarBrands/nissan.png';

const MotionBox = motion(Box);
const MotionImage = motion(Image);

const AvailableCarBrands = () => {
  const bgColor = useColorModeValue("gray.50", "gray.900");
  const cardBg = useColorModeValue("white", "gray.800");
  const textColor = useColorModeValue("gray.600", "gray.300");
  const headingColor = useColorModeValue("gray.800", "white");
  const borderColor = useColorModeValue("gray.200", "gray.700");

  const brands = [
    { name: 'Toyota', logo: Toyota, founded: 1937, country: 'Japan', popular: true },
    { name: 'Honda', logo: Honda, founded: 1948, country: 'Japan', popular: true },
    { name: 'Daihatsu', logo: Diahatsu, founded: 1907, country: 'Japan', popular: false },
    { name: 'BMW', logo: BMW, founded: 1916, country: 'Germany', popular: true },
    { name: 'Mercedes', logo: Mercedes, founded: 1926, country: 'Germany', popular: true },
    { name: 'Audi', logo: Audi, founded: 1909, country: 'Germany', popular: true },
    { name: 'Suzuki', logo: Suzuki, founded: 1909, country: 'Japan', popular: true },
    { name: 'Chevrolet', logo: Chevroltet, founded: 1911, country: 'USA', popular: false },
    { name: 'Hyundai', logo: Hyundai, founded: 1967, country: 'South Korea', popular: true },
    { name: 'MG', logo: Mg, founded: 1924, country: 'UK/China', popular: false },
    { name: 'Peugeot', logo: Peugeot, founded: 1810, country: 'France', popular: false },
    { name: 'Nissan', logo: Nissan, founded: 1933, country: 'Japan', popular: true }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.05,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { y: 30, opacity: 0, scale: 0.9 },
    visible: {
      y: 0,
      opacity: 1,
      scale: 1,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 12
      }
    }
  };

  return (
    <Box py={20} bg={bgColor} overflow="hidden">
      <Container maxW="1200px" mx="auto" px={4}>
        {/* Header Section */}
        <VStack spacing={4} mb={12} textAlign="center">
          <Badge
            colorScheme="blue"
            fontSize="sm"
            px={4}
            py={2}
            borderRadius="full"
            variant="solid"
            bgGradient="linear(to-r, blue.500, purple.500)"
          >
            Trusted Brands
          </Badge>
          <Heading
            as="h2"
            fontSize={{ base: "3xl", md: "4xl", lg: "5xl" }}
            fontWeight="bold"
            bgGradient="linear(to-r, blue.600, purple.600, pink.600)"
            bgClip="text"
            letterSpacing="tight"
          >
            Available Car Brands
          </Heading>
          <Text fontSize={{ base: "md", md: "lg" }} color={textColor} maxW="600px">
            Explore our extensive collection of premium automobile brands from around the world
          </Text>
          <HStack spacing={2} mt={2}>
            <Icon as={FaCar} color="blue.500" />
            <Text fontSize="sm" color="blue.600" fontWeight="semibold">
              {brands.length}+ Trusted Brands
            </Text>
          </HStack>
        </VStack>

        {/* Brands Grid */}
        <SimpleGrid
          columns={{ base: 2, sm: 3, md: 4, lg: 6 }}
          spacing={{ base: 4, md: 6, lg: 8 }}
          as={motion.div}
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-50px" }}
        >
          {brands.map((brand, index) => (
            <MotionBox
              key={index}
              variants={itemVariants}
              whileHover={{ 
                y: -8,
                scale: 1.05,
                transition: { duration: 0.2 }
              }}
              bg={cardBg}
              borderRadius="2xl"
              overflow="hidden"
              boxShadow="lg"
              border="1px solid"
              borderColor={borderColor}
              position="relative"
              cursor="pointer"
              _hover={{
                boxShadow: "2xl",
                borderColor: "blue.200"
              }}
              transition="all 0.3s"
            >
              {/* Popular Badge */}
              {brand.popular && (
                <Box
                  position="absolute"
                  top={2}
                  right={2}
                  zIndex={1}
                >
                  <Badge
                    colorScheme="yellow"
                    variant="solid"
                    borderRadius="full"
                    px={2}
                    py={1}
                    fontSize="xs"
                  >
                    <HStack spacing={1}>
                      <Icon as={FaStar} boxSize={2} />
                      <Text fontSize="xs">Popular</Text>
                    </HStack>
                  </Badge>
                </Box>
              )}

              {/* Brand Image */}
              <Box
                position="relative"
                h="140px"
                bg="white"
                display="flex"
                alignItems="center"
                justifyContent="center"
                p={4}
                borderBottom="1px solid"
                borderColor={borderColor}
              >
                <MotionImage
                  src={brand.logo}
                  alt={brand.name}
                  objectFit="contain"
                  maxH="100px"
                  maxW="100%"
                  whileHover={{ scale: 1.1 }}
                  transition={{ duration: 0.3 }}
                  fallbackSrc="https://via.placeholder.com/100x100?text=Car+Logo"
                />
              </Box>

              {/* Brand Info */}
              <Box p={4} textAlign="center">
                <Text
                  fontWeight="bold"
                  fontSize="md"
                  color={headingColor}
                  mb={1}
                  letterSpacing="tight"
                >
                  {brand.name}
                </Text>
                <Text fontSize="xs" color={textColor} opacity={0.8}>
                  Since {brand.founded}
                </Text>
                <Text fontSize="xs" color={textColor} opacity={0.6} mt={1}>
                  {brand.country}
                </Text>
              </Box>
            </MotionBox>
          ))}
        </SimpleGrid>

        {/* Trust Indicators */}
        <Box mt={16} textAlign="center">
          <HStack
            spacing={{ base: 4, md: 8 }}
            justify="center"
            flexWrap="wrap"
            gap={4}
          >
            {[
              { icon: FaCheckCircle, text: "Verified Brands", color: "green.500" },
              { icon: FaCar, text: "1000+ Vehicles", color: "blue.500" },
              { icon: FaStar, text: "Trusted by 10K+ Customers", color: "yellow.500" }
            ].map((item, idx) => (
              <HStack key={idx} spacing={2}>
                <Icon as={item.icon} color={item.color} boxSize={4} />
                <Text fontSize="sm" color={textColor}>{item.text}</Text>
              </HStack>
            ))}
          </HStack>
        </Box>

        {/* CTA Banner */}
        <Box
          mt={16}
          p={8}
          borderRadius="2xl"
          bgGradient="linear(to-r, blue.500, purple.500)"
          textAlign="center"
          position="relative"
          overflow="hidden"
        >
          <Box
            position="absolute"
            top={0}
            left={0}
            right={0}
            bottom={0}
            opacity={0.1}
            bgImage="radial-gradient(circle at 20% 50%, rgba(255,255,255,0.2) 0%, transparent 50%)"
          />
          <VStack spacing={3} position="relative" zIndex={1}>
            <Text color="white" fontSize="lg" fontWeight="bold">
              Looking for a specific brand?
            </Text>
            <Text color="white" opacity={0.9} fontSize="sm">
              We have all major car brands available for sale, rent, and ride sharing
            </Text>
            <HStack spacing={4} mt={2}>
              <Text color="white" fontSize="sm" fontWeight="semibold">
                Explore All Brands →
              </Text>
            </HStack>
          </VStack>
        </Box>
      </Container>
    </Box>
  );
};

export default AvailableCarBrands;